import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {FilterValue, TimeFilterValue} from 'app/features/analytics/service/filter-value.interface';
import {SavedFilterNodes, SavedFilterService} from 'app/features/analytics/service/saved-filter.service';
import {Dimension, SavedDateFilterPeriods} from '../../../../../../../../globalTypes';
import {DimensionsAndMeasuresService} from 'app/features/analytics/service/dimensions-and-measures.service';
import {FilterValueService} from 'app/features/analytics/service/filter-value.service';
import {DropDownDirective} from 'app/shared/directives/drop-down.directive';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {FormService} from 'app/core/services/form.service';
import {formatDate} from 'app/features/analytics/helpers/dates';
import {of, Subject} from 'rxjs';
import {takeUntil, switchMap} from 'rxjs/operators';
import {DefaultSavedReportService} from 'app/features/analytics/features/reports/service/default-saved-report.service';
import {cloneDeep, isEqualDeep} from '../../../../../../../core/helpers/object';
import {isTimeDimension} from 'app/features/analytics/shared/utils';

enum innerModal {
    SAVE_AS= 'saveAs',
    OVERWRITE = 'overwrite',
    RENAME = 'rename',
}

@Component({
    selector: 'save-reports-filters',
    templateUrl: './saved-report-form.component.html',
    styleUrls: ['./saved-report-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SavedReportFormComponent implements OnInit, OnDestroy {
    shakeButton = false;
    activityDate: string = Dimension.DATE;
    installDate: string = Dimension.INSTALL_DATE;
    canOverwrite = false;
    canSave = false;

    loading$ = new Subject<boolean>();

    openedModal: innerModal = null;
    modalStates = innerModal

    @Input() filterPeriods: SavedDateFilterPeriods;
    @Output() filterSelected: EventEmitter<{ id: string; label: any }> = new EventEmitter<{ id: string; label: any }>();
    @ViewChild(DropDownDirective) dropDown: DropDownDirective;

    filterPeriodsType = Object.values(SavedDateFilterPeriods);

    form: FormGroup;
    renameForm: FormGroup;

    filterList: SavedFilterNodes[] = [];

    private destroy$ = new Subject<void>();

    constructor (
        public savedFilters: SavedFilterService,
        public defaultSavedReport: DefaultSavedReportService,
        public dms: DimensionsAndMeasuresService,
        private val: FilterValueService,
        private fb: FormBuilder,
        public formService: FormService,
        public cd: ChangeDetectorRef
    ) {}

    ngOnInit (): void {
        this.form = this.newFilter();
        this.renameForm = this.renameFormInit();

        this.savedFilters.getSavedFilters()
            .pipe(takeUntil(this.destroy$))
            .subscribe(filters => {
                this.filterList = filters;
                this.isSavedReport();
                this.checkCanSave();
            });

        this.val.value$
            .pipe(takeUntil(this.destroy$))
            .subscribe(value => {
                this.isSavedReport();
                this.checkCanSave();
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    get filters () {
        return this.val.value$.getValue().filter;
    }

    hasDate (typeDate: string) {
        return this.filters.some(e => e.id === typeDate);
    }

    getDate (typeDate: string) {
        // @ts-ignore
        let filterDate: TimeFilterValue = this.filters.find((t: TimeFilterValue) => t.id === typeDate);
        return `${formatDate(filterDate.from)} - ${formatDate(filterDate.to)}`;
    }

    newFilter (): FormGroup {
        return this.fb.group({
            name: ['', Validators.required],
            isDefault: [false],
            activityFilter: [null],
            installFilter: [null]
        });
    }

    renameFormInit (): FormGroup {
        return this.fb.group({
            name: ['', Validators.required],
        });
    }

    setFilterDefault (filterId: string) {
        if (!filterId) {
            return of(null);
        }
        return this.defaultSavedReport.makeDefaultReport(filterId);
    }

    onSubmit (event: Event) {
        event.preventDefault();
        this.loading$.next(true);
        this.formService
            .submit(this.savedFilters.create({
                ...this.form.value,
                filters: {...this.val.getFiltersForSave()}
            })).pipe(switchMap((filter: any) => {
                if (this.form.value.isDefault) {
                    return this.setFilterDefault(filter?.reportFilters?.saveFilters?.id).pipe(switchMap(() => of(filter)));
                } else {
                    return of(filter);
                }
                
            }))
            .subscribe(
                (filter: any) => {
                    console.warn(filter, 'filter saved!');
                    this.resetModal();
                    if (filter?.reportFilters?.saveFilters) {
                        let filterWithNewDate = this.savedFilters.getFilterWithNewDate(filter.reportFilters.saveFilters);
                        this.val.onGetNewFilterFromQuery(filterWithNewDate);
                    }
                    this.loading$.next(false);
                    this.cd.markForCheck();
                },
                err => {
                    console.warn(err, 'failed save filter!');
                    this.loading$.next(false);
                    this.formService.catchError(err);
                }
            );
    }

    onOverwrite(event: Event) {
        event.preventDefault();
        this.loading$.next(true);
        this.savedFilters.update({
                id: this.val.value.savedReportId,
                filters: {...this.val.getFiltersForSave()}
            })
            .subscribe(
                filter => {
                    console.warn(filter, 'filter saved!');
                    this.resetModal();
                    this.loading$.next(false);
                },
                err => {
                    console.warn(err, 'failed save filter!');
                    this.formService.catchError(err);
                    this.loading$.next(false);
                }
            );
    }

    onRename(event: Event) {
        event.preventDefault();
        this.loading$.next(true);
        this.formService
            .submit(this.savedFilters.rename({
                name: this.renameForm.controls.name.value,
                id: this.val.value.savedReportId,
            }))
            .subscribe(
                filter => {
                    console.warn(filter, 'filter updated!');
                    this.dropDown.toggleContent(false);
                    this.renameForm.reset();
                    this.resetModal();
                    this.loading$.next(false);
                },
                err => {
                    console.warn(err, 'failed save filter!');
                    this.formService.catchError(err);
                    this.loading$.next(false);
                }
            );
    }

    cancelSaveFilterTemplate (e: Event) {
        e.preventDefault();
        this.resetModal();
    }

    resetModal() {
        this.dropDown.toggleContent(false);
        this.shakeButton = false;
        this.openedModal = null;
        this.form.reset();
        this.cd.detectChanges();
    }

    shakeCancel (e: Event) {
        if (!this.openedModal) {
            return this.cancelSaveFilterTemplate(e);
        }

        this.shakeButton = !this.shakeButton;
    }

    async openSaveAsModel() {
        await this.dropDown.toggleContent();
        this.formService.init(this.form, {checkClientValidity: true});
        this.openedModal = innerModal.SAVE_AS;
        await this.dropDown.toggleContent();
        this.cd.detectChanges();
    }

    async openRenameModal() {
        await this.dropDown.toggleContent();
        this.openedModal = innerModal.RENAME;
        this.formService.init(this.renameForm, {checkClientValidity: true});
        this.renameForm.patchValue({name : this.getFilterName()});
        await this.dropDown.toggleContent();
        this.cd.detectChanges();
    }

    async openOverwrite() {
        await this.dropDown.toggleContent();
        this.openedModal = innerModal.OVERWRITE;
        await this.dropDown.toggleContent();
    }

    isSavedReport() {
        const filterValue = this.val.value$.value;
        this.canOverwrite = !!this.filterList.find((filter: SavedFilterNodes) => filter.id === filterValue.savedReportId);
        this.cd.markForCheck();
    }

    isSaved() {
        const selectedFilter = cloneDeep(this.val.value);
        const savedFilterState = cloneDeep(this.filterList.find(filter => filter.id === this.val.value.savedReportId)?.filters);
        
        if (selectedFilter && savedFilterState) {
            return isEqualDeep(this.takeComparingParts(selectedFilter),this.takeComparingParts(savedFilterState));
        }

        return false;
    }

    takeComparingParts(filter) {
        return {
            filter: filter.filter,
            groupBy: filter.groupBy,
            measures: filter.measures && filter.measures.length ? filter.measures.map(measure => {id: measure.id; day: measure.day; label: measure.label}) : [],
            chartWithMetrics: filter.chartWithMetrics,
            colorFormatting: filter.colorFormatting,
        }
    }

    getFilterName(): string {
        return this.filterList.find(filter => filter.id === this.val.value.savedReportId)?.name;
    }

    checkCanSave() {
        const value = this.val.value;
        this.canSave = !!(value.filter?.length && value.measures?.length && this.hasTimeFilter(value.filter));
        this.cd.markForCheck();
    }

    hasTimeFilter(filters: FilterValue[]) {
        return !filters.every(filter => !isTimeDimension(filter.id));
    }
}
